import { JWT_TOKEN, setShop, setToken } from '../utils/helpers';
import {
  standaloneCredentials,
  standaloneOrderId,
} from './standaloneQrSession';

// #149: the client token and shop of the portal, read at request time. After
// a reload `JWT_TOKEN` is empty until the first refresh (App), so the pages
// that may be reloaded (withdrawal form, receipt, case C continuation) read
// the shared storage the lookup wrote and install it in memory once. A
// full-screen QR session reads only its own in-memory session (#72).
export interface PortalCredentials {
  token: string;
  shopId: number;
}

export const currentCredentials = (
  standalone: boolean
): PortalCredentials | null => {
  if (standalone) return standaloneCredentials();
  let token: string | null = null;
  let shopId = 0;
  try {
    token = localStorage.getItem('jwtToken');
    shopId = Number(localStorage.getItem('shopId'));
  } catch {
    return null;
  }
  if (!token || !Number.isInteger(shopId) || shopId <= 0) return null;
  if (JWT_TOKEN === '') {
    setToken(token);
    setShop(shopId);
  }
  return { token, shopId };
};

// #154 (Codex r1 #2, r2): the portal session of the lookup as ONE atomic
// record {token, shopId, orderId} under `portalSession`, written in a single
// operation by the lookup and rewritten whole by the token refresh. The
// legacy keys (`jwtToken`, `shopId`) are still written through: the token
// refresh, the shop start-up and the declaration / receipt reads use them,
// and a tab of an older portal writes only them.
export const PORTAL_SESSION_KEY = 'portalSession';

export interface PortalSessionRecord {
  token: string;
  shopId: number;
  orderId: number;
}

const isId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

export const readPortalSession = (): PortalSessionRecord | null => {
  try {
    const raw = localStorage.getItem(PORTAL_SESSION_KEY);
    if (raw === null) return null;
    const record = JSON.parse(raw) as Partial<PortalSessionRecord> | null;
    return record &&
      typeof record.token === 'string' &&
      record.token !== '' &&
      isId(record.shopId) &&
      isId(record.orderId)
      ? { token: record.token, shopId: record.shopId, orderId: record.orderId }
      : null;
  } catch {
    return null;
  }
};

// the record first (one write), then the legacy keys
export const storePortalSession = (record: PortalSessionRecord): void => {
  localStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify(record));
  localStorage.setItem('jwtToken', record.token);
  localStorage.setItem('shopId', record.shopId.toString());
};

// the token refresh: the record of the refreshed token is rewritten whole
// with the new token; a record of another token (another session) is left
// as it is
export const refreshPortalSession = (
  previousToken: string,
  token: string
): void => {
  const record = readPortalSession();
  if (record === null || record.token !== previousToken) return;
  try {
    localStorage.setItem(
      PORTAL_SESSION_KEY,
      JSON.stringify({ ...record, token })
    );
  } catch {
    // no storage: the continuation then asks for a new search
  }
};

// #154: the credentials of the session of THIS order, for the case C
// continuation (loader, uploads, submission). Shared storage: ONLY the
// atomic record is trusted, and only while the legacy token is the record's
// own - a record and a legacy token that disagree are a replacement in
// progress in another tab, or a tab of an older portal that replaced the
// legacy keys only: no session (session expired). The full-screen QR session
// is bound to its order in memory. Another shop or order: no session.
export const credentialsForOrder = (
  standalone: boolean,
  shopId: number,
  orderId: number
): PortalCredentials | null => {
  if (standalone) {
    const credentials = standaloneCredentials();
    return credentials !== null &&
      credentials.shopId === shopId &&
      standaloneOrderId() === orderId
      ? credentials
      : null;
  }
  const record = readPortalSession();
  let legacyToken: string | null;
  try {
    legacyToken = localStorage.getItem('jwtToken');
  } catch {
    return null;
  }
  if (
    record === null ||
    record.token !== legacyToken ||
    record.shopId !== shopId ||
    record.orderId !== orderId
  ) {
    return null;
  }
  if (JWT_TOKEN === '') {
    setToken(record.token);
    setShop(record.shopId);
  }
  return { token: record.token, shopId: record.shopId };
};
